import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  MapPin, Building2, Users, FileText, Upload, CheckCircle2,
  ChevronRight, ChevronLeft, Sparkles, Globe, Search, Shield,
  ArrowRight, Zap, Target, Lock, Truck, Calendar, AlertTriangle,
  Clock, Eye, Home, Map, FileUp, Settings
} from "lucide-react";

const STEPS = [
  { id: 'basics', label: 'Project basics', icon: Building2 },
  { id: 'site', label: 'Site intelligence', icon: Map },
  { id: 'model', label: 'Operating model', icon: Settings },
  { id: 'people', label: 'People', icon: Users },
  { id: 'scope', label: 'Scope', icon: Target },
  { id: 'documents', label: 'Documents', icon: FileUp },
  { id: 'summary', label: 'AI summary', icon: Sparkles },
];

const PROJECT_TYPES = [
  { id: 'custom-home', label: 'Custom home' },
  { id: 'townhouse', label: 'Townhouse' },
  { id: 'duplex', label: 'Duplex' },
  { id: 'spec-home', label: 'Spec home' },
  { id: 'renovation', label: 'Renovation / Extension' },
  { id: 'apartment', label: 'Apartment / Multi-res' },
];

const STAGES = [
  { id: 'pre-start', label: 'Pre-Start' },
  { id: 'pre-slab', label: 'Pre-Slab' },
  { id: 'pre-frame', label: 'Pre-Frame' },
  { id: 'pre-oc', label: 'Pre-OC' },
  { id: 'handover', label: 'Handover' },
];

const SITE_INTEL = {
  address: '27 Banksia Ave, Werribee VIC 3030',
  parcel: { lot: '12', plan: 'PS723456', size: '620 sqm', frontage: '15.5m' },
  council: 'Wyndham City Council',
  lga: 'City of Wyndham',
  zone: 'General Residential Zone (GRZ1)',
  overlay: 'Design and Development Overlay (DDO8)',
  siteAppearance: 'Appears vacant / cleared lot',
  buildContext: 'Likely new build',
  planningContext: 'ResCode applies. Max height 9m / 2 storeys. Garden area requirement 35%.',
  confidence: 91,
};

function StepIndicator({ steps, current, onStep }) {
  return (
    <div className="flex items-center gap-0 w-full mb-6" data-testid="step-indicator">
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = s.icon;
        return (
          <div key={s.id} className="flex items-center flex-1">
            <button onClick={() => i <= current && onStep(i)}
              className={`flex-1 text-center py-2.5 px-2 rounded-xl border-2 transition-all ${active ? 'border-blue-500 bg-blue-50 shadow-md' : done ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'} ${i <= current ? 'cursor-pointer' : 'cursor-default'}`}
              data-testid={`step-${s.id}`}>
              <Icon className={`w-4 h-4 mx-auto mb-0.5 ${active ? 'text-blue-600' : done ? 'text-emerald-600' : 'text-slate-400'}`} />
              <p className={`text-[9px] font-bold uppercase tracking-wider ${active ? 'text-blue-700' : done ? 'text-emerald-700' : 'text-slate-400'}`}>{s.label}</p>
            </button>
            {i < steps.length - 1 && <ChevronRight className={`w-4 h-4 shrink-0 mx-0.5 ${done ? 'text-emerald-400' : 'text-slate-300'}`} />}
          </div>
        );
      })}
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, ...props }) {
  return <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300 transition-all" {...props} />;
}

function SelectGrid({ options, value, onChange, cols = 3 }) {
  return (
    <div className={`grid grid-cols-${cols} gap-2`}>
      {options.map(o => (
        <button key={o.id} onClick={() => onChange(o.id)}
          className={`text-left px-3 py-2.5 rounded-lg border-2 transition-all text-sm font-medium ${value === o.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function ProjectIntake() {
  const { createProject, workspace } = useDemoState();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [detecting, setDetecting] = useState(false);
  const [detected, setDetected] = useState(false);
  const [form, setForm] = useState({
    name: '', address: '27 Banksia Ave', suburb: 'Werribee', state: 'VIC', postcode: '3030',
    projectType: 'custom-home', currentStage: 'pre-slab',
    operatingModel: 'consultant-led', planningLodgement: 'consultant', permitPaperwork: 'builder',
    clientSuppliesFinishes: false, ownerAppointedTrades: false, useQR: true,
    commsChannel: 'email-whatsapp-qr', internalReviewer: '', tradeApprover: '',
    pm: '', admin: '', coordinator: '', supervisor: '', consultant: '', clientName: '',
    builderScope: 'All structural works, services rough-in, painting, fit-off',
    excludedItems: 'Fencing, driveway crossover, landscaping beyond approved plan',
    ownerSupplied: 'Floor tiles (bathroom + kitchen), kitchen tapware',
    ownerAppointed: 'Tiler, cabinet maker',
    riskAreas: 'Client tile selection timeline, cabinet maker booking',
    documents: [],
  });

  const upd = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleDetect = () => {
    setDetecting(true);
    setTimeout(() => { setDetecting(false); setDetected(true); }, 1800);
  };

  const handleCreate = () => {
    const id = createProject({
      name: form.name || `${form.address} Residence`,
      address: form.address, suburb: form.suburb, state: form.state, postcode: form.postcode,
      projectType: form.projectType, stage: STAGES.find(s => s.id === form.currentStage)?.label || 'Pre-Start',
      operatingModel: form.operatingModel, pm: form.pm, council: SITE_INTEL.council,
    });
    toast.success('Project created', { description: `${form.name || form.address} added to ${workspace.name}` });
    setStep(STEPS.length - 1);
  };

  const canNext = step < STEPS.length - 1;
  const canPrev = step > 0;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-5" data-testid="project-intake-page">
        <div>
          <h1 className="text-xl font-heading font-bold text-slate-900">Create project</h1>
          <p className="text-sm text-slate-500">Set up the address, operating model, ownership boundaries, and approval workflow for a new job.</p>
        </div>

        <StepIndicator steps={STEPS} current={step} onStep={setStep} />

        {/* Step 1: Basics */}
        {step === 0 && (
          <Card className="border-slate-200" data-testid="step-basics">
            <CardContent className="py-5 px-5 space-y-4">
              <FieldGroup label="Project / job name"><Input value={form.name} onChange={v => upd('name', v)} placeholder="e.g. 27 Banksia Ave Residence" data-testid="input-name" /></FieldGroup>
              <div className="grid grid-cols-2 gap-3">
                <FieldGroup label="Street address"><Input value={form.address} onChange={v => upd('address', v)} placeholder="27 Banksia Ave" data-testid="input-address" /></FieldGroup>
                <FieldGroup label="Suburb"><Input value={form.suburb} onChange={v => upd('suburb', v)} placeholder="Werribee" /></FieldGroup>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FieldGroup label="State"><Input value={form.state} onChange={v => upd('state', v)} placeholder="VIC" /></FieldGroup>
                <FieldGroup label="Postcode"><Input value={form.postcode} onChange={v => upd('postcode', v)} placeholder="3030" /></FieldGroup>
              </div>
              <FieldGroup label="Project type"><SelectGrid options={PROJECT_TYPES} value={form.projectType} onChange={v => upd('projectType', v)} /></FieldGroup>
              <FieldGroup label="Stage currently at"><SelectGrid options={STAGES} value={form.currentStage} onChange={v => upd('currentStage', v)} cols={5} /></FieldGroup>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Site Intelligence */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-site-intelligence">
            <Card className="border-slate-200">
              <CardContent className="py-5 px-5">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{form.address}, {form.suburb} {form.state} {form.postcode}</p>
                    <p className="text-xs text-slate-400">Address entered in Step 1</p>
                  </div>
                  {!detected && (
                    <Button size="sm" className="ml-auto bg-blue-600 hover:bg-blue-500 gap-1.5" onClick={handleDetect} disabled={detecting} data-testid="detect-btn">
                      {detecting ? <Clock className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                      {detecting ? 'Detecting...' : 'Detect site context'}
                    </Button>
                  )}
                  {detected && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 ml-auto">Detected</Badge>}
                </div>

                {detected && (
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    {/* Map panel */}
                    <div className="lg:col-span-3 bg-slate-100 rounded-xl overflow-hidden border border-slate-200 relative" style={{ minHeight: 300 }} data-testid="site-map-panel">
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/30 to-blue-50/30" />
                      <svg viewBox="0 0 400 300" className="w-full h-full">
                        <rect width="400" height="300" fill="#e8f0e4" />
                        <rect x="40" y="20" width="320" height="260" fill="#f0f4f0" rx="4" />
                        {/* Streets */}
                        <rect x="0" y="130" width="400" height="16" fill="#d4d8d4" />
                        <rect x="180" y="0" width="14" height="300" fill="#d4d8d4" />
                        <rect x="80" y="60" width="100" height="70" fill="#c8e0c0" stroke="#8fbc8f" strokeWidth="1" rx="2" />
                        <rect x="220" y="60" width="100" height="70" fill="#c8e0c0" stroke="#8fbc8f" strokeWidth="1" rx="2" />
                        <rect x="80" y="160" width="100" height="70" fill="#c8e0c0" stroke="#8fbc8f" strokeWidth="1" rx="2" />
                        <rect x="220" y="160" width="100" height="70" fill="#c8e0c0" stroke="#8fbc8f" strokeWidth="1" rx="2" />
                        {/* Subject site highlight */}
                        <rect x="100" y="170" width="60" height="50" fill="#3b82f6" fillOpacity="0.2" stroke="#3b82f6" strokeWidth="2" rx="2" strokeDasharray="4 2" />
                        <circle cx="130" cy="195" r="6" fill="#2563eb" />
                        <circle cx="130" cy="195" r="12" fill="none" stroke="#2563eb" strokeWidth="1.5" opacity="0.5" />
                        {/* Labels */}
                        <text x="200" y="142" textAnchor="middle" fontSize="8" fill="#666" fontWeight="bold">BANKSIA AVE</text>
                        <text x="190" y="55" textAnchor="middle" fontSize="7" fill="#888" transform="rotate(90 190 55)">WATTLE ST</text>
                        <text x="130" y="230" textAnchor="middle" fontSize="7" fill="#2563eb" fontWeight="bold">Subject site</text>
                        <text x="270" y="100" textAnchor="middle" fontSize="6" fill="#999">Council: Wyndham</text>
                      </svg>
                      <div className="absolute top-2 left-2 bg-white/90 backdrop-blur rounded-lg px-2.5 py-1.5 shadow-sm border border-slate-200">
                        <p className="text-[9px] font-semibold text-blue-600 uppercase tracking-wider">Parcel boundary</p>
                        <p className="text-[10px] text-slate-600">Lot {SITE_INTEL.parcel.lot} / {SITE_INTEL.parcel.plan}</p>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur rounded-lg px-2.5 py-1.5 shadow-sm border border-slate-200">
                        <p className="text-[9px] text-slate-400">Simulated map view</p>
                      </div>
                    </div>

                    {/* Intelligence panel */}
                    <div className="lg:col-span-2 space-y-2" data-testid="site-intel-panel">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1"><Sparkles className="w-3.5 h-3.5 text-blue-600" /><span className="text-[10px] font-bold text-blue-700 uppercase">Site confidence</span></div>
                        <p className="text-2xl font-heading font-bold text-blue-700">{SITE_INTEL.confidence}%</p>
                      </div>
                      {[
                        { label: 'Standardised address', value: SITE_INTEL.address, tag: 'Detected' },
                        { label: 'Parcel / lot', value: `Lot ${SITE_INTEL.parcel.lot} on ${SITE_INTEL.parcel.plan}`, tag: 'Found' },
                        { label: 'Lot size', value: `${SITE_INTEL.parcel.size} (${SITE_INTEL.parcel.frontage} frontage)`, tag: 'Estimated' },
                        { label: 'Council / LGA', value: SITE_INTEL.council, tag: 'Identified' },
                        { label: 'Zone', value: SITE_INTEL.zone, tag: 'Detected' },
                        { label: 'Overlay', value: SITE_INTEL.overlay, tag: 'Detected' },
                        { label: 'Site appearance', value: SITE_INTEL.siteAppearance, tag: 'Suggested' },
                        { label: 'Build context', value: SITE_INTEL.buildContext, tag: 'Appears to be' },
                      ].map((r, i) => (
                        <div key={i} className="bg-white rounded-lg border border-slate-100 p-2.5">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">{r.label}</span>
                            <Badge className="text-[8px] bg-emerald-50 text-emerald-600 border-emerald-200">{r.tag}</Badge>
                          </div>
                          <p className="text-xs font-medium text-slate-800">{r.value}</p>
                        </div>
                      ))}
                      <div className="bg-slate-50 rounded-lg border border-slate-200 p-2.5">
                        <p className="text-[9px] font-semibold text-slate-400 uppercase mb-1">Planning context</p>
                        <p className="text-[10px] text-slate-600 leading-relaxed">{SITE_INTEL.planningContext}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Operating Model */}
        {step === 2 && (
          <Card className="border-slate-200" data-testid="step-operating-model">
            <CardContent className="py-5 px-5 space-y-4">
              <FieldGroup label="Who owns planning lodgement?">
                <SelectGrid options={[{ id: 'consultant', label: 'Consultant' }, { id: 'builder', label: 'Builder' }, { id: 'client', label: 'Client' }]} value={form.planningLodgement} onChange={v => upd('planningLodgement', v)} /></FieldGroup>
              <FieldGroup label="Who manages building permit paperwork?">
                <SelectGrid options={[{ id: 'builder', label: 'Builder' }, { id: 'consultant', label: 'Consultant' }, { id: 'admin', label: 'Office admin' }]} value={form.permitPaperwork} onChange={v => upd('permitPaperwork', v)} /></FieldGroup>
              <FieldGroup label="Workflow type">
                <SelectGrid options={[{ id: 'consultant-led', label: 'Consultant-led' }, { id: 'internal-managed', label: 'Builder-managed' }, { id: 'developer-led', label: 'Developer-led' }]} value={form.operatingModel} onChange={v => upd('operatingModel', v)} /></FieldGroup>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <FieldGroup label="Client supplies finishes?">
                  <SelectGrid options={[{ id: true, label: 'Yes' }, { id: false, label: 'No' }]} value={form.clientSuppliesFinishes} onChange={v => upd('clientSuppliesFinishes', v)} cols={2} /></FieldGroup>
                <FieldGroup label="Owner-appointed trades?">
                  <SelectGrid options={[{ id: true, label: 'Yes' }, { id: false, label: 'No' }]} value={form.ownerAppointedTrades} onChange={v => upd('ownerAppointedTrades', v)} cols={2} /></FieldGroup>
              </div>
              <FieldGroup label="Communication channels">
                <SelectGrid options={[{ id: 'email-only', label: 'Email only' }, { id: 'email-whatsapp', label: 'Email + WhatsApp' }, { id: 'email-whatsapp-qr', label: 'Email + WhatsApp + QR' }]} value={form.commsChannel} onChange={v => upd('commsChannel', v)} /></FieldGroup>
            </CardContent>
          </Card>
        )}

        {/* Step 4: People */}
        {step === 3 && (
          <Card className="border-slate-200" data-testid="step-people">
            <CardContent className="py-5 px-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <FieldGroup label="Builder PM"><Input value={form.pm} onChange={v => upd('pm', v)} placeholder="e.g. Sarah Chen" /></FieldGroup>
                <FieldGroup label="Office admin"><Input value={form.admin} onChange={v => upd('admin', v)} placeholder="e.g. Lisa Nguyen" /></FieldGroup>
                <FieldGroup label="Permit coordinator"><Input value={form.coordinator} onChange={v => upd('coordinator', v)} placeholder="e.g. Tom Bradley" /></FieldGroup>
                <FieldGroup label="Site supervisor"><Input value={form.supervisor} onChange={v => upd('supervisor', v)} placeholder="e.g. James Wheeler" /></FieldGroup>
                <FieldGroup label="Primary consultant"><Input value={form.consultant} onChange={v => upd('consultant', v)} placeholder="e.g. David Patel" /></FieldGroup>
                <FieldGroup label="Client / owner name"><Input value={form.clientName} onChange={v => upd('clientName', v)} placeholder="e.g. The Morrison Family" /></FieldGroup>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Scope */}
        {step === 4 && (
          <Card className="border-slate-200" data-testid="step-scope">
            <CardContent className="py-5 px-5 space-y-4">
              <FieldGroup label="Builder scope (included items)">
                <textarea value={form.builderScope} onChange={e => upd('builderScope', e.target.value)} rows={2} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200" /></FieldGroup>
              <FieldGroup label="Excluded items">
                <textarea value={form.excludedItems} onChange={e => upd('excludedItems', e.target.value)} rows={2} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200" /></FieldGroup>
              <FieldGroup label="Owner-supplied items">
                <textarea value={form.ownerSupplied} onChange={e => upd('ownerSupplied', e.target.value)} rows={2} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200" /></FieldGroup>
              <FieldGroup label="Owner-appointed trades">
                <textarea value={form.ownerAppointed} onChange={e => upd('ownerAppointed', e.target.value)} rows={2} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200" /></FieldGroup>
              <FieldGroup label="Likely risk areas">
                <textarea value={form.riskAreas} onChange={e => upd('riskAreas', e.target.value)} rows={2} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200" /></FieldGroup>
            </CardContent>
          </Card>
        )}

        {/* Step 6: Documents */}
        {step === 5 && (
          <Card className="border-slate-200" data-testid="step-documents">
            <CardContent className="py-5 px-5 space-y-4">
              <p className="text-sm text-slate-600">Upload starting documents, or skip for now.</p>
              <div className="grid grid-cols-2 gap-3">
                {['Permit decision PDF', 'Contract summary', 'Consultant pack', 'Initial plans / reports', 'Client selections list', 'Site photos'].map(doc => (
                  <div key={doc} className="flex items-center gap-3 p-3 border border-dashed border-slate-300 rounded-lg hover:border-blue-300 hover:bg-blue-50/30 transition-all cursor-pointer group" onClick={() => toast.info(`${doc} upload simulated`)}>
                    <Upload className="w-4 h-4 text-slate-400 group-hover:text-blue-500" />
                    <span className="text-xs font-medium text-slate-600 group-hover:text-blue-600">{doc}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => { toast.info('Skipped for now'); setStep(6); }} className="text-xs text-slate-400 hover:text-slate-600 underline">Skip for now</button>
            </CardContent>
          </Card>
        )}

        {/* Step 7: AI Summary */}
        {step === 6 && (
          <div className="space-y-4" data-testid="step-summary">
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardContent className="py-5 px-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
                  <div>
                    <h3 className="text-base font-heading font-bold text-emerald-800">Project ready to create</h3>
                    <p className="text-xs text-emerald-600">Review the summary below, then create your project.</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Project', value: form.name || `${form.address} Residence` },
                    { label: 'Address', value: `${form.address}, ${form.suburb} ${form.state}` },
                    { label: 'Council', value: detected ? SITE_INTEL.council : 'Not detected' },
                    { label: 'Type', value: PROJECT_TYPES.find(t => t.id === form.projectType)?.label },
                    { label: 'Stage', value: STAGES.find(s => s.id === form.currentStage)?.label },
                    { label: 'Model', value: form.operatingModel },
                    { label: 'Comms', value: form.commsChannel },
                    { label: 'Site', value: detected ? SITE_INTEL.siteAppearance : 'Not checked' },
                  ].map((r, i) => (
                    <div key={i} className="bg-white rounded-lg border border-slate-100 p-2.5">
                      <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">{r.label}</p>
                      <p className="text-xs font-medium text-slate-800">{r.value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* AI risk flags */}
            <Card className="border-blue-200 bg-blue-50/20">
              <CardContent className="py-4 px-5">
                <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-blue-600" /><span className="text-xs font-bold text-blue-700">AI Setup Assessment</span></div>
                <div className="space-y-2">
                  {form.ownerAppointed && <div className="flex items-start gap-2 text-xs text-blue-700"><AlertTriangle className="w-3 h-3 mt-0.5 text-amber-500 shrink-0" />Owner-appointed trades detected. Recommend setting up Scope Boundary Ledger early.</div>}
                  {form.ownerSupplied && <div className="flex items-start gap-2 text-xs text-blue-700"><Clock className="w-3 h-3 mt-0.5 text-amber-500 shrink-0" />Owner-supplied items detected. Add due dates to prevent trade delays.</div>}
                  <div className="flex items-start gap-2 text-xs text-blue-700"><CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-500 shrink-0" />{form.commsChannel === 'email-whatsapp-qr' ? 'Full communication channels enabled. Site uploads via QR available.' : 'Communication channels configured.'}</div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2 shadow-lg" onClick={handleCreate} data-testid="create-project-btn">
                <Zap className="w-4 h-4" /> Create project
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/builder')}>Cancel</Button>
            </div>

            {/* Recommended first pages */}
            <Card className="border-slate-200">
              <CardContent className="py-4 px-5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Recommended first pages after creation</p>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                  {[
                    { path: '/builder/decision-import', label: 'Decision Import', icon: FileText, desc: 'Import permit decision' },
                    { path: '/builder/stage-conditions', label: 'Stage Conditions', icon: Shield, desc: 'Review extracted conditions' },
                    { path: '/builder/scope-boundary-ledger', label: 'Scope Boundary', icon: Target, desc: 'Set owner boundaries' },
                    { path: '/builder/trade-release', label: 'Trade Release', icon: Truck, desc: 'Configure trade partners' },
                    { path: '/shared/client-portal', label: 'Client Portal', icon: Globe, desc: 'Set up client view' },
                    { path: '/builder/stage-readiness', label: 'Stage Readiness', icon: Lock, desc: 'Check readiness state' },
                  ].map(l => (
                    <button key={l.path} onClick={() => navigate(l.path)} className="flex items-center gap-2.5 p-3 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-200 hover:bg-blue-50/30 transition-all text-left group">
                      <l.icon className="w-4 h-4 text-slate-400 group-hover:text-blue-600 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-slate-700 group-hover:text-blue-600">{l.label}</p>
                        <p className="text-[10px] text-slate-400">{l.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation */}
        {step < 6 && (
          <div className="flex items-center justify-between pt-2">
            <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={!canPrev} className="gap-1.5" data-testid="intake-prev-btn">
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
            <span className="text-xs text-slate-400">Step {step + 1} of {STEPS.length}</span>
            <Button onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))} disabled={!canNext} className="bg-blue-600 hover:bg-blue-500 gap-1.5" data-testid="intake-next-btn">
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
